import React, { useEffect, useState } from "react";
import { sum } from "lodash";
import { Modal } from "@mui/material";
import {
    InfoText,
    ModalWrapper,
    TitleText,
    TitleWrapper,
    ButtonRow,
    Expandable,
    StyledBackdrop,
} from "./Styled";
import { api } from "../../../../../../shared/hooks/api";
import CustomTable from "../../../../../../shared/components/CustomTable";
import { Material } from "../../../../../../data/entities";
import { SearchBarWithSingleSelectSuggestion } from "../../../../../../shared/components/SearchBar/SearchBarWithSuggestion";

import { StyledButton } from "../../../../../../shared/styled/TitleBar";
import { MaterialRow, MaterialTableColumns } from "../index";
import { formatCurrency } from "../../../../../../utils/format";

interface AddMaterialModalProps {
    selectedMaterials: Material[];
    open: boolean;
    handleClose: any;
    mergeMaterialOrder: Function;
}

export const AddMaterialModal = ({
    selectedMaterials,
    open,
    handleClose,
    mergeMaterialOrder,
}: AddMaterialModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [{ data, variables, error }, fetchMaterials] = api.get(
        `/materials?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`,
        undefined,
        true
    );
    const [selectedItems, setSelectedItems] =
        useState<Material[]>(selectedMaterials);
    const [showValidationHighlight, setShowValidationHighlight] = useState(false);

    const materials = data && data.results ? data.results : [];
    const summaryMaterials = {
        id: `Tổng định mức vật tư`,
        price: formatCurrency(
            sum(
                selectedItems.map((item: any) => {
                    const price = Number(((item.provider_price || 0) * 1000000).toFixed(6));
                    const quantity = Number(((item.total_measurement || 0) * 1000000).toFixed(6));
                    const excess_quantity = Number(((item.excess_quantity || 0) * 1000000).toFixed(6));
                    const excess_price = Number(((item.excess_price || 0) * 1000000).toFixed(6));

                    const mainPrice = Number(((price * quantity) / 1000000000000).toFixed(6));
                    const excessTotal = Number(((excess_price * excess_quantity) / 1000000000000).toFixed(6));

                    return mainPrice - excessTotal;
                })
            )
        ),
        isSummary: true,
    };

    const isMissingProvider = (material: any) => {
        return !material.provider_name && !material.isSummary;
    };

    const tableItems =
        selectedItems.length === 0
            ? []
            : [...selectedItems, summaryMaterials].map((item, idx) => ({
                  ...item,
                  localId: `${item.id}-${idx}`,
                  missingProvider: isMissingProvider(item),
              }));

    const removeSelectedItem = (localId: string) => {
        const removedSelectedItems = selectedItems.filter(
            (selectedItem, idx) => `${selectedItem.id}-${idx}` !== localId
        );
        setSelectedItems(removedSelectedItems);
    };

    const updateItemSelectedQuantity = (
        localId: string,
        selectedQuantity: number
    ) => {
        const updatedSelectedItems = selectedItems.map(
            (material: any, idx: number) => {
                if (`${material.id}-${idx}` === localId)
                    return { ...material, quantity: selectedQuantity };
                return material;
            }
        );
        setSelectedItems(updatedSelectedItems);
    };

    const handleOnclickItem = (item: Material, isAdded: boolean) => {
        setSelectedItems([...selectedItems, { ...item, quantity: 0 }]);
    };

    const validateMaterials = () => {
        // Skip validation for summary row
        const materialsToValidate = selectedItems.filter((item: any) => !item.isSummary);
        
        if (materialsToValidate.length === 0) {
            return false;
        }

        const materialsWithoutProvider = materialsToValidate.filter(
            (material: any) => !material.provider_name
        );

        if (materialsWithoutProvider.length > 0) {
            return false;
        }

        return true;
    };

    const onClickAddMaterials = () => {
        const isValid = validateMaterials();
        
        if (!isValid) {
            setShowValidationHighlight(true);
            return;
        }
        
        mergeMaterialOrder({ materials: selectedItems });
        handleClose();
    };

    const onChangeProvider = (localId: string, provider: any) => {
        const updatedMaterials = selectedItems.map((material: any, idx) => {
            if (`${material.id}-${idx}` === localId) {
                const selectedProvider = material.providers.find(
                    (p: any) => p.provider.name === provider.provider.name
                );
                return {
                    ...material,
                    provider_name: provider.provider.name,
                    provider_price: selectedProvider ? selectedProvider.price : 0
                };
            }
            return material;
        });
        setSelectedItems(updatedMaterials);
    };

    const onUpdateMaterialNote = (localId: string, note: string) => {
        const updatedMaterials = selectedItems.map((material: any, idx) => {
            if (`${material.id}-${idx}` === localId)
                return { ...material, note: note };
            return material;
        });
        setSelectedItems(updatedMaterials);
    };

    const onChangeMaterialType = (localId: string, type: string) => {
        const updatedMaterials = selectedItems.map((material: any, idx) => {
            if (`${material.id}-${idx}` === localId) {
                return { ...material, category: type };
            }
            return material;
        });
        setSelectedItems(updatedMaterials);
    };

    const onChangeExcessQuantity = (localId: string, quantity: any) => {
        const updatedMaterials = selectedItems.map(
            (material: any, idx: number) => {
                if (`${material.id}-${idx}` === localId)
                    return { ...material, excess_quantity: quantity };
                return material;
            }
        );
        setSelectedItems(updatedMaterials);
    };

    const onChangeExcessPrice = (localId: string, quantity: any) => {
        const updatedMaterials = selectedItems.map(
            (material: any, idx: number) => {
                if (`${material.id}-${idx}` === localId)
                    return { ...material, excess_price: quantity };
                return material;
            }
        );
        setSelectedItems(updatedMaterials);
    };

    const onUpdateMaterialCategoryNote = (localId: string, note: string) => {
        const updatedMaterials = selectedItems.map((material: any, idx) => {
            if (`${material.id}-${idx}` === localId)
                return { ...material, category_note: note };
            return material;
        });
        setSelectedItems(updatedMaterials);
    };

    const onUpdateSpecificationNote = (localId: string, note: string) => {
        const updatedMaterials = selectedItems.map((material: any, idx) => {
            if (`${material.id}-${idx}` === localId)
                return { ...material, specification: note };
            return material;
        });

        setSelectedItems(updatedMaterials);
    };

    const onChangeMeasurement = (localId: string, quantity: any) => {
        const updatedMaterials = selectedItems.map(
            (material: any, idx: number) => {
                if (`${material.id}-${idx}` === localId)
                    return { ...material, measurement: quantity };
                return material;
            }
        );
        setSelectedItems(updatedMaterials);
    };

    const onChangeMaterialTotalMeasurement = (
        localId: string,
        quantity: any
    ) => {
        const updatedMaterials = selectedItems.map(
            (material: any, idx: number) => {
                if (`${material.id}-${idx}` === localId)
                    return { ...material, total_measurement: quantity };
                return material;
            }
        );
        setSelectedItems(updatedMaterials);
    };

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        } else {
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={isOpen}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Thêm vật tư</TitleText>
                        <InfoText>Vui lòng lựa chọn vật tư</InfoText>
                    </TitleWrapper>

                    <SearchBarWithSingleSelectSuggestion
                        menuList={materials}
                        fetchItems={(variables: any) =>
                            fetchMaterials({
                                ...variables,
                            })
                        }
                        handleOnclickItem={handleOnclickItem}
                    />

                    <CustomTable
                        itemList={tableItems}
                        RowElement={MaterialRow}
                        TableColumns={MaterialTableColumns}
                        extraRowProps={{
                            removeItem: removeSelectedItem,
                            onChangeMaterialQuantity:
                                updateItemSelectedQuantity,
                            onChangeMaterialType,
                            onChangeProvider,
                            onUpdateMaterialNote,
                            onChangeExcessQuantity,
                            onChangeExcessPrice,
                            onUpdateMaterialCategoryNote,
                            onUpdateSpecificationNote,
                            onChangeMeasurement,
                            onChangeMaterialTotalMeasurement,
                            highlightMissingProvider: showValidationHighlight,
                        }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                </Expandable>

                <ButtonRow>
                    <StyledButton onClick={handleCloseWithAnimation}>
                        Huỷ bỏ
                    </StyledButton>
                    <StyledButton
                        variant="contained"
                        onClick={onClickAddMaterials}
                    >
                        Lưu vật tư
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};
